/**
 * RealUIProvider - DOM spotlight/tooltip control
 *
 * Implements UIProvider by manipulating the real DOM to highlight,
 * scroll, and interact with page elements.
 */

import { UIProvider } from '../types';

export class RealUIProvider implements UIProvider {
  private highlights: Map<string, () => void> = new Map();

  async highlightElement(
    selector: string,
    options?: { type?: 'spotlight' | 'outline' | 'pulse'; color?: string; duration?: number }
  ): Promise<() => void> {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (!el) {
      console.warn(`[RealUI] Element not found: ${selector}`);
      return () => {};
    }

    // Store original styles
    const original = {
      outline: el.style.outline,
      outlineOffset: el.style.outlineOffset,
      transition: el.style.transition,
    };

    // Apply highlight based on type
    const type = options?.type || 'outline';
    const color = options?.color || 'rgba(99, 102, 241, 0.6)';

    if (type === 'outline' || type === 'spotlight') {
      el.style.outline = `2px solid ${color}`;
      el.style.outlineOffset = '4px';
      el.style.transition = 'outline 0.3s ease';
    } else if (type === 'pulse') {
      el.style.outline = `2px solid ${color}`;
      el.style.outlineOffset = '4px';
    }

    const cleanup = () => {
      el.style.outline = original.outline;
      el.style.outlineOffset = original.outlineOffset;
      el.style.transition = original.transition;
    };

    this.highlights.set(selector, cleanup);
    return cleanup;
  }

  removeHighlight(highlightId: string): void {
    const cleanup = this.highlights.get(highlightId);
    if (cleanup) {
      cleanup();
      this.highlights.delete(highlightId);
    }
  }

  async showTooltip(
    _selector: string,
    _content: string,
    _position?: 'top' | 'bottom' | 'left' | 'right'
  ): Promise<() => void> {
    // Tooltip rendering is handled by FlowTooltip React component
    // This is a no-op since the overlay system manages tooltips
    return () => {};
  }

  async scrollToElement(
    selector: string,
    options?: { behavior?: 'smooth' | 'auto'; block?: 'start' | 'center' | 'end' }
  ): Promise<void> {
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({
        behavior: options?.behavior || 'smooth',
        block: options?.block || 'center',
      });
    }
  }

  disableInteractions(): void {
    // Not needed in overlay mode — the spotlight mask handles pointer events
  }

  enableInteractions(): void {
    // Not needed in overlay mode
  }

  simulateClick(selector: string): void {
    const el = document.querySelector(selector) as HTMLElement | null;
    if (el) {
      el.click();
    }
  }

  elementExists(selector: string): boolean {
    return document.querySelector(selector) !== null;
  }

  async waitForElement(selector: string, timeout: number = 5000): Promise<Element> {
    // Check if already exists
    const existing = document.querySelector(selector);
    if (existing) return existing;

    return new Promise((resolve, reject) => {
      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) {
          observer.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element not found within ${timeout}ms: ${selector}`));
      }, timeout);
    });
  }
}
